import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Package } from 'lucide-react';
import {
  loginWithEmailPin,
  refreshAuthSession,
  getPostLoginPath
} from '../services/authService';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from 'components/ui/card';
import { cn } from 'lib/utils';

const PIN_LENGTH = 4;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(() => Array(PIN_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [existingSession, setExistingSession] = useState(null);
  const pinRefs = useRef([]);

  const from = location.state?.from || '/';
  const pin = digits.join('');
  const pinComplete = pin.length === PIN_LENGTH && digits.every((d) => d !== '');

  useEffect(() => {
    document.documentElement.classList.add('app-login-mode');
    return () => document.documentElement.classList.remove('app-login-mode');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await refreshAuthSession();
      if (cancelled) return;
      setExistingSession(session);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const focusPin = (index) => {
    const el = pinRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const applyDigits = (nextDigits) => {
    const normalized = nextDigits
      .slice(0, PIN_LENGTH)
      .map((d) => (/\d/.test(d) ? d : ''));
    while (normalized.length < PIN_LENGTH) normalized.push('');
    setDigits(normalized);
    return normalized;
  };

  const handlePinChange = (index, raw) => {
    const value = String(raw).replace(/\D/g, '');

    if (value.length > 1) {
      const next = [...digits];
      const chars = value.slice(0, PIN_LENGTH - index).split('');
      chars.forEach((ch, i) => {
        next[index + i] = ch;
      });
      const applied = applyDigits(next);
      const nextEmpty = applied.findIndex((d, i) => i >= index && d === '');
      focusPin(nextEmpty === -1 ? PIN_LENGTH - 1 : nextEmpty);
      return;
    }

    const next = [...digits];
    next[index] = value.slice(-1);
    applyDigits(next);
    if (value && index < PIN_LENGTH - 1) {
      focusPin(index + 1);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        applyDigits(next);
      } else if (index > 0) {
        e.preventDefault();
        const next = [...digits];
        next[index - 1] = '';
        applyDigits(next);
        focusPin(index - 1);
      }
      return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusPin(index - 1);
    }
    if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
      e.preventDefault();
      focusPin(index + 1);
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '')
      .replace(/\D/g, '')
      .slice(0, PIN_LENGTH);
    if (!pasted) return;
    const next = Array(PIN_LENGTH)
      .fill('')
      .map((_, i) => pasted[i] || '');
    applyDigits(next);
    focusPin(Math.min(pasted.length, PIN_LENGTH - 1));
  };

  if (checking) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6">
        <Card className="w-full max-w-sm border-0 shadow-none bg-transparent">
          <CardContent className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Checking session…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (existingSession) {
    return <Navigate to={getPostLoginPath(existingSession, from)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pinComplete) {
      setError('Please enter your 4-digit PIN.');
      focusPin(digits.findIndex((d) => d === '') || 0);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const session = await loginWithEmailPin(email, pin);
      navigate(getPostLoginPath(session, from), { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      applyDigits(Array(PIN_LENGTH).fill(''));
      focusPin(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-background p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_hsl(var(--primary)_/_0.12),_transparent_55%)]"
      />

      <Card className="relative z-10 w-full max-w-sm border-0 bg-card/80 shadow-xl shadow-black/5 backdrop-blur-sm">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <Package className="size-6" />
          </div>
          <div className="space-y-1.5">
            <CardTitle className="text-2xl">Sagemcom</CardTitle>
            <CardDescription>Sign in with your email and PIN</CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                placeholder="you@sagemcom.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label id="login-pin-label">PIN</Label>
              <div
                className="grid grid-cols-4 gap-2"
                role="group"
                aria-labelledby="login-pin-label"
                onPaste={handlePinPaste}
              >
                {digits.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => {
                      pinRefs.current[index] = el;
                    }}
                    id={index === 0 ? 'login-pin' : undefined}
                    type="password"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    pattern="[0-9]*"
                    aria-label={`PIN digit ${index + 1} of ${PIN_LENGTH}`}
                    value={digit}
                    disabled={submitting}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    onFocus={(e) => e.target.select()}
                    className={cn(
                      'h-12 px-0 text-center text-lg font-semibold tracking-widest',
                      'caret-primary'
                    )}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full border-0 shadow-none ring-0"
              size="lg"
              disabled={submitting || !pinComplete}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex-col">
          <p className="text-center text-xs text-muted-foreground">
            Fleet staff and drivers use the email and PIN issued by your administrator.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

export default Login;